import { PageClassSelector } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { lessonRunsApi, reportsApi } from '../../../services/apiV2';

interface WorkHistoryStudent {
  id: number;
  name: string;
  email: string;
  grade_level?: string;
  student_id?: string;
}

interface WorkHistorySection {
  id: number;
  name: string;
  group_name?: string;
  academic_year?: string;
  grade_level_at_enrollment?: string;
}

interface WorkHistoryItem {
  id: string;
  type: 'live_run' | 'makeup_assignment' | 'quiz' | 'evidence_assignment' | 'external_task' | string;
  type_label: string;
  status: string;
  status_label: string;
  title: string;
  hook_title?: string;
  section_id: number;
  section_name: string;
  lesson_run_id?: number;
  assignment_id?: number;
  external_task_id?: number;
  delivery_mode?: string;
  source_app?: string;
  assigned_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  activity_at?: string | null;
  due_at?: string | null;
  work_date?: string;
  detail?: string;
  metrics?: Record<string, any>;
  detail_url?: string;
}

interface WorkHistoryPayload {
  student: WorkHistoryStudent;
  sections: WorkHistorySection[];
  items: WorkHistoryItem[];
  summary: {
    total: number;
    live_runs: number;
    assignments: number;
    external_tasks: number;
    completed: number;
    not_completed: number;
  };
}

interface ReviewStep {
  id: number;
  order: number;
  title: string;
  runner_phase?: string;
  step_kind?: string;
  prompt_text?: string;
  requires_submission?: boolean;
  submission_status: 'submitted' | 'no_submission' | 'not_required' | string;
  understanding_signal?: {
    level: string;
    label: string;
    teacher_action: string;
    reason: string;
  };
  signal?: {
    response?: Record<string, any>;
    response_text?: string;
    reasoning_text?: string;
    confidence_level?: number | null;
    is_correct?: boolean | null;
    submitted_at?: string;
  } | null;
  signal_attempts?: Array<{
    id: number;
    response?: Record<string, any>;
    response_text?: string;
    reasoning_text?: string;
    confidence_level?: number | null;
    is_correct?: boolean | null;
    submitted_at?: string;
  }>;
  evidence?: {
    status?: string;
    evidence_type?: string;
    content?: Record<string, any>;
    preview_text?: string;
    submitted_at?: string;
  } | null;
  evidence_attempts?: Array<{
    id: number;
    status?: string;
    evidence_type?: string;
    content?: Record<string, any>;
    preview_text?: string;
    submitted_at?: string;
  }>;
  answer_key_lines?: string[];
}

const GRADE_OPTIONS = ['', '6th', '7th', '8th', '9th', '10th', '11th', '12th'];

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusClass = (status?: string) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') return 'bg-emerald-100 text-emerald-800';
  if (normalized === 'in_progress') return 'bg-amber-100 text-amber-800';
  if (normalized === 'not_completed' || normalized === 'not_started') return 'bg-rose-100 text-rose-800';
  return 'bg-slate-100 text-slate-700';
};

const typeClass = (type?: string) => {
  const normalized = String(type || '').toLowerCase();
  if (normalized === 'live_run') return 'bg-sky-100 text-sky-800';
  if (normalized === 'quiz') return 'bg-violet-100 text-violet-800';
  if (normalized === 'external_task') return 'bg-teal-100 text-teal-800';
  if (normalized === 'evidence_assignment') return 'bg-indigo-100 text-indigo-800';
  return 'bg-orange-100 text-orange-800';
};

const understandingClass = (level?: string) => {
  const normalized = String(level || '').toLowerCase();
  if (normalized === 'strong') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (normalized === 'partial' || normalized === 'submitted') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (normalized === 'weak' || normalized === 'intervention') return 'bg-rose-100 text-rose-800 border-rose-200';
  if (normalized === 'missing') return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const extractResponseText = (payload?: Record<string, any>) => {
  if (!payload || typeof payload !== 'object') return '';
  const direct = String(payload.reasoning_text || payload.reasoning || payload.value || '').trim();
  if (direct) return direct;
  if (Array.isArray(payload.selected_indices)) return `Selected: ${payload.selected_indices.join(', ')}`;
  if (Array.isArray(payload.ordering)) return `Order: ${payload.ordering.join(' -> ')}`;
  if (payload.pairs && typeof payload.pairs === 'object') {
    return Object.entries(payload.pairs)
      .map(([left, right]) => `${left} -> ${right}`)
      .join(' | ');
  }
  return Object.keys(payload).length ? JSON.stringify(payload, null, 2) : '';
};

const extractEvidenceText = (payload?: Record<string, any>) => {
  if (!payload || typeof payload !== 'object') return '';
  return String(payload.text || payload.url || '').trim() || (Object.keys(payload).length ? JSON.stringify(payload, null, 2) : '');
};

export const V2StudentWorkHistory: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_reports');
  const { studentId } = useParams<{ studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [payload, setPayload] = useState<WorkHistoryPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRunReview, setSelectedRunReview] = useState<any | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');

  const [sectionId, setSectionId] = useState((searchParams.get('section') || searchParams.get('section_id') || '').replace(/^all$/, ''));
  const [gradeLevel, setGradeLevel] = useState(searchParams.get('grade_level') || '');
  const [workType, setWorkType] = useState(searchParams.get('type') || 'all');
  const [workStatus, setWorkStatus] = useState(searchParams.get('status') || 'all');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [fromDate, setFromDate] = useState(searchParams.get('from') || '');
  const [toDate, setToDate] = useState(searchParams.get('to') || '');

  const selectedRunId = searchParams.get('run_id');

  const loadHistory = async (refresh = false) => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const data = await reportsApi.studentWorkHistory(Number(studentId), {
        section_id: sectionId ? Number(sectionId) : undefined,
        grade_level: gradeLevel || undefined,
        type: workType,
        status: workStatus,
        q: query.trim() || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        refresh,
      });
      setPayload(data as WorkHistoryPayload);
    } catch (err: any) {
      setPayload(null);
      setError(err?.response?.data?.error || 'Failed to load student work history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadHistory(false);
  }, [studentId, sectionId, gradeLevel, workType, workStatus, fromDate, toDate]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadHistory(false);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set('section', sectionId || 'all');
    if (gradeLevel) next.set('grade_level', gradeLevel);
    if (workType !== 'all') next.set('type', workType);
    if (workStatus !== 'all') next.set('status', workStatus);
    if (query.trim()) next.set('q', query.trim());
    if (fromDate) next.set('from', fromDate);
    if (toDate) next.set('to', toDate);
    if (selectedRunId) next.set('run_id', selectedRunId);
    setSearchParams(next, { replace: true });
  }, [sectionId, gradeLevel, workType, workStatus, query, fromDate, toDate, selectedRunId, setSearchParams]);

  useEffect(() => {
    const loadReview = async () => {
      if (!selectedRunId || !studentId) {
        setSelectedRunReview(null);
        setReviewError('');
        return;
      }
      setReviewLoading(true);
      setReviewError('');
      try {
        const review = await lessonRunsApi.review(Number(selectedRunId), Number(studentId));
        setSelectedRunReview(review);
      } catch (err: any) {
        setSelectedRunReview(null);
        setReviewError(err?.response?.data?.error || 'Failed to load live lesson review.');
      } finally {
        setReviewLoading(false);
      }
    };
    void loadReview();
  }, [selectedRunId, studentId]);

  const selectedRunItem = useMemo(
    () => payload?.items?.find((item) => String(item.lesson_run_id || '') === String(selectedRunId || '')) || null,
    [payload, selectedRunId],
  );

  const openRunReview = (runId: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('run_id', String(runId));
    setSearchParams(next);
  };

  const closeRunReview = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('run_id');
    setSearchParams(next);
  };

  const reviewSteps = Array.isArray(selectedRunReview?.steps)
    ? [...selectedRunReview.steps].sort((a: ReviewStep, b: ReviewStep) => Number(a.order || 0) - Number(b.order || 0))
    : [];

  const reviewSummary = useMemo(() => {
    const required = reviewSteps.filter((step: ReviewStep) => Boolean(step.requires_submission));
    const submitted = required.filter((step: ReviewStep) => step.submission_status === 'submitted');
    const missing = required.filter((step: ReviewStep) => step.submission_status !== 'submitted');
    const intervention = reviewSteps.filter((step: ReviewStep) => ['weak', 'intervention', 'missing'].includes(String(step.understanding_signal?.level || '')));
    const strong = reviewSteps.filter((step: ReviewStep) => step.understanding_signal?.level === 'strong');
    return {
      required: required.length,
      submitted: submitted.length,
      missing: missing.length,
      intervention: intervention.length,
      strong: strong.length,
    };
  }, [reviewSteps]);

  return (
    <OptionalAppShell
      enabled={redesignEnabled}
      role="teacher"
      navItems={teacherV2NavItems('students')}
      title="Student Work"
    >
      {!redesignEnabled && <TeacherNav activeTab="students" />}
      <div className={redesignEnabled ? 'mx-auto max-w-7xl space-y-4' : 'mx-auto max-w-7xl p-6'}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Student Work History</p>
            <h1 className="text-2xl font-semibold text-gray-900">{payload?.student?.name || 'Student Work'}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {payload?.student?.email || ''}
              {payload?.student?.grade_level ? ` | ${payload.student.grade_level}` : ''}
              {payload?.student?.student_id ? ` | ID ${payload.student.student_id}` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/v2/teacher/students" className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700">
              Back to Students
            </Link>
            <button
              type="button"
              className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
              onClick={() => void loadHistory(true)}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-7">
            <PageClassSelector><label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700">Section</span>
              <select className="rounded border border-gray-300 px-3 py-2" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
                <option value="">All sections</option>
                {(payload?.sections || []).map((section) => (
                  <option key={section.id} value={String(section.id)}>
                    {section.name}
                    {section.academic_year ? ` · ${section.academic_year}` : ''}
                    {section.grade_level_at_enrollment ? ` · ${section.grade_level_at_enrollment}` : ''}
                  </option>
                ))}
              </select>
            </label></PageClassSelector>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700">Grade</span>
              <select className="rounded border border-gray-300 px-3 py-2" value={gradeLevel} onChange={(e) => setGradeLevel(e.target.value)}>
                {GRADE_OPTIONS.map((grade) => (
                  <option key={grade || 'all'} value={grade}>{grade || 'All grades'}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700">Type</span>
              <select className="rounded border border-gray-300 px-3 py-2" value={workType} onChange={(e) => setWorkType(e.target.value)}>
                <option value="all">All work</option>
                <option value="live_run">Live lessons</option>
                <option value="makeup_assignment">Make-up</option>
                <option value="quiz">Quizzes</option>
                <option value="evidence_assignment">Evidence</option>
                <option value="external_task">External</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700">Status</span>
              <select className="rounded border border-gray-300 px-3 py-2" value={workStatus} onChange={(e) => setWorkStatus(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="completed">Completed</option>
                <option value="in_progress">In progress</option>
                <option value="not_started">Not started</option>
                <option value="not_completed">Not completed</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700">From</span>
              <input type="date" className="rounded border border-gray-300 px-3 py-2" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700">To</span>
              <input type="date" className="rounded border border-gray-300 px-3 py-2" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-700">Search</span>
              <input
                type="search"
                className="rounded border border-gray-300 px-3 py-2"
                placeholder="Lesson, task, section"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="rounded border border-gray-200 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Total Items</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{payload?.summary?.total || 0}</p>
          </div>
          <div className="rounded border border-gray-200 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Live Lessons</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{payload?.summary?.live_runs || 0}</p>
          </div>
          <div className="rounded border border-gray-200 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Assignments</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{payload?.summary?.assignments || 0}</p>
          </div>
          <div className="rounded border border-gray-200 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">External</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{payload?.summary?.external_tasks || 0}</p>
          </div>
          <div className="rounded border border-gray-200 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Completed</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{payload?.summary?.completed || 0}</p>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.85fr)]">
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">Work Timeline</h2>
            </div>
            {loading ? (
              <p className="px-4 py-6 text-sm text-gray-500">Loading work history...</p>
            ) : !payload?.items?.length ? (
              <p className="px-4 py-6 text-sm text-gray-500">No work items match the current filters.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {payload.items.map((item) => (
                  <article key={item.id} className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${typeClass(item.type)}`}>
                            {item.type_label}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}>
                            {item.status_label}
                          </span>
                        </div>
                        <h3 className="mt-2 text-base font-semibold text-gray-900">{item.title}</h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {item.section_name}
                          {item.hook_title ? ` | ${item.hook_title}` : ''}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Assigned {formatDateTime(item.assigned_at)}
                          {item.due_at ? ` | Due ${formatDate(item.due_at)}` : ''}
                          {item.completed_at ? ` | Completed ${formatDateTime(item.completed_at)}` : ''}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {item.type === 'live_run' && item.lesson_run_id && (
                          <button
                            type="button"
                            className="rounded border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium text-sky-700"
                            onClick={() => openRunReview(item.lesson_run_id!)}
                          >
                            Review live work
                          </button>
                        )}
                        {item.detail_url && item.type !== 'live_run' && (
                          item.detail_url.startsWith('/v2') ? (
                            <Link to={item.detail_url} className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700">
                              Open details
                            </Link>
                          ) : (
                            <a href={item.detail_url} target="_blank" rel="noreferrer" className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700">
                              Open link
                            </a>
                          )
                        )}
                      </div>
                    </div>
                    {item.metrics && (
                      <div className="mt-3 grid gap-2 sm:grid-cols-3">
                        {Object.entries(item.metrics).slice(0, 6).map(([key, value]) => (
                          <div key={`${item.id}-${key}`} className="rounded border border-gray-100 bg-gray-50 px-2 py-1">
                            <p className="text-[11px] uppercase tracking-wide text-gray-500">{key.replace(/_/g, ' ')}</p>
                            <p className="text-sm font-medium text-gray-800">{String(value)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {item.detail && (
                      <p className="mt-3 whitespace-pre-wrap rounded border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {item.detail}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>

          <aside className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Live Lesson Review</h2>
                <p className="text-xs text-gray-500">
                  {selectedRunItem ? selectedRunItem.title : 'Select a live lesson from the timeline.'}
                </p>
              </div>
              {selectedRunId && (
                <button type="button" className="text-xs font-medium text-gray-600 hover:text-gray-900" onClick={closeRunReview}>
                  Clear
                </button>
              )}
            </div>
            {!selectedRunId ? (
              <p className="px-4 py-6 text-sm text-gray-500">No live lesson selected.</p>
            ) : reviewLoading ? (
              <p className="px-4 py-6 text-sm text-gray-500">Loading live lesson review...</p>
            ) : reviewError ? (
              <p className="px-4 py-6 text-sm text-rose-700">{reviewError}</p>
            ) : reviewSteps.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500">No step review data is available.</p>
            ) : (
              <div className="max-h-[760px] overflow-y-auto">
                <div className="grid gap-2 border-b border-gray-100 p-4 sm:grid-cols-2">
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Responses</p>
                    <p className="text-sm font-semibold text-gray-900">{reviewSummary.submitted}/{reviewSummary.required} required</p>
                  </div>
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Missing</p>
                    <p className="text-sm font-semibold text-gray-900">{reviewSummary.missing}</p>
                  </div>
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Strong Evidence</p>
                    <p className="text-sm font-semibold text-gray-900">{reviewSummary.strong}</p>
                  </div>
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Needs Attention</p>
                    <p className="text-sm font-semibold text-gray-900">{reviewSummary.intervention}</p>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                {reviewSteps.map((step: ReviewStep) => {
                  const responseText = String(step.signal?.response_text || '').trim() || extractResponseText(step.signal?.response);
                  const reasoningText = String(step.signal?.reasoning_text || '').trim();
                  const evidenceText = String(step.evidence?.preview_text || '').trim() || extractEvidenceText(step.evidence?.content);
                  const attempts = Array.isArray(step.signal_attempts) ? step.signal_attempts : [];
                  const olderAttempts = attempts.slice(1);
                  const evidenceAttempts = Array.isArray(step.evidence_attempts) ? step.evidence_attempts : [];
                  const olderEvidenceAttempts = evidenceAttempts.slice(1);
                  const signal = step.understanding_signal || {
                    level: 'context',
                    label: 'Context step',
                    teacher_action: 'No response expected',
                    reason: '',
                  };
                  return (
                    <article key={step.id} className={`p-4 ${step.requires_submission ? 'bg-white' : 'bg-slate-50/60'}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">
                            Step {Number(step.order || 0) + 1}
                            {step.runner_phase ? ` | ${step.runner_phase}` : ''}
                          </p>
                          <h3 className="mt-1 text-sm font-semibold text-gray-900">{step.title || `Step ${step.order}`}</h3>
                          <p className="mt-1 text-xs text-gray-500">
                            {step.requires_submission ? 'Student response expected' : 'Instruction/context step'}
                          </p>
                        </div>
                        <div className="flex flex-col items-start gap-1 sm:items-end">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(step.submission_status === 'submitted' ? 'completed' : 'not_completed')}`}>
                            {String(step.submission_status || '').replace('_', ' ')}
                          </span>
                          <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${understandingClass(signal.level)}`}>
                            {signal.label}
                          </span>
                        </div>
                      </div>
                      {step.prompt_text && (
                        <div className="mt-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Prompt / task</p>
                          <p className="mt-1 whitespace-pre-wrap rounded border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800">{step.prompt_text}</p>
                        </div>
                      )}
                      {responseText ? (
                        <div className="mt-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Student answer</p>
                          <pre className="mt-1 whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800">{responseText}</pre>
                        </div>
                      ) : null}
                      {reasoningText && reasoningText !== responseText ? (
                        <div className="mt-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Reasoning</p>
                          <pre className="mt-1 whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800">{reasoningText}</pre>
                        </div>
                      ) : null}
                      {step.signal && (
                        <div className="mt-3 grid gap-2 sm:grid-cols-3">
                          <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1">
                            <p className="text-[11px] uppercase tracking-wide text-gray-500">Confidence</p>
                            <p className="text-sm font-medium text-gray-800">{step.signal.confidence_level ?? 'Not provided'}</p>
                          </div>
                          <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1">
                            <p className="text-[11px] uppercase tracking-wide text-gray-500">Correctness</p>
                            <p className="text-sm font-medium text-gray-800">
                              {step.signal.is_correct === true ? 'Correct' : step.signal.is_correct === false ? 'Incorrect' : 'Not automatically scored'}
                            </p>
                          </div>
                          <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1">
                            <p className="text-[11px] uppercase tracking-wide text-gray-500">Submitted</p>
                            <p className="text-sm font-medium text-gray-800">{formatDateTime(step.signal.submitted_at)}</p>
                          </div>
                        </div>
                      )}
                      {evidenceText ? (
                        <div className="mt-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Evidence</p>
                          <pre className="mt-1 whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800">{evidenceText}</pre>
                        </div>
                      ) : null}
                      {Array.isArray(step.answer_key_lines) && step.answer_key_lines.length > 0 && (
                        <details className="mt-3 text-xs text-gray-700">
                          <summary className="cursor-pointer font-medium text-emerald-700">Show answer key / expected answer</summary>
                          <div className="mt-2 space-y-1">
                            {step.answer_key_lines.map((line, index) => (
                              <div key={`${step.id}-answer-${index}`} className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
                                {line}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      <div className={`mt-3 rounded border px-3 py-2 text-xs ${understandingClass(signal.level)}`}>
                        <p className="font-semibold">Teacher read: {signal.teacher_action}</p>
                        {signal.reason && <p className="mt-1">{signal.reason}</p>}
                      </div>
                      {(olderAttempts.length > 0 || olderEvidenceAttempts.length > 0) && (
                        <details className="mt-3 text-xs text-gray-700">
                          <summary className="cursor-pointer font-medium">Show previous attempts</summary>
                          <div className="mt-2 space-y-2">
                            {olderAttempts.map((attempt) => (
                              <div key={`signal-attempt-${step.id}-${attempt.id}`} className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                                <div className="text-gray-500">
                                  Live response | {formatDateTime(attempt.submitted_at)}
                                  {attempt.confidence_level ? ` | Confidence ${attempt.confidence_level}` : ''}
                                  {attempt.is_correct === true ? ' | Correct' : attempt.is_correct === false ? ' | Incorrect' : ''}
                                </div>
                                <pre className="mt-1 whitespace-pre-wrap text-gray-800">
                                  {String(attempt.response_text || '').trim() || extractResponseText(attempt.response)}
                                </pre>
                              </div>
                            ))}
                            {olderEvidenceAttempts.map((attempt) => (
                              <div key={`evidence-attempt-${step.id}-${attempt.id}`} className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                                <div className="text-gray-500">Evidence | {formatDateTime(attempt.submitted_at)} | {attempt.status || 'submitted'}</div>
                                <pre className="mt-1 whitespace-pre-wrap text-gray-800">
                                  {String(attempt.preview_text || '').trim() || extractEvidenceText(attempt.content)}
                                </pre>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      {!responseText && !evidenceText && (
                        <p className="mt-3 text-xs text-gray-500">
                          {step.requires_submission ? 'No submitted work captured for this required step.' : 'No student response was expected for this step.'}
                        </p>
                      )}
                    </article>
                  );
                })}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </OptionalAppShell>
  );
};

export default V2StudentWorkHistory;
